import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button, Card, Text } from "@andrum/ui";
import { submitFeedback } from "@/features/learn/api/feedback-api";
import { markNpsDismissed, markNpsSubmitted } from "@/lib/engagement/nps";

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Soft NPS modal — always portaled to document.body with z above bottom nav
 * so it never sits under the tab bar (stacking-context safe).
 */
export function NpsSheet({ open, onClose }: Props) {
  const { t } = useTranslation();
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) {
      setScore(null);
      setComment("");
      setThanks(false);
      setBusy(false);
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !mounted) return null;

  const submit = async () => {
    if (score == null || busy) return;
    setBusy(true);
    const result = await submitFeedback({
      source: "nps",
      rating: score,
      body: comment.trim() || null,
    });
    setBusy(false);
    if (result.ok) {
      await markNpsSubmitted();
      setThanks(true);
      window.setTimeout(() => onClose(), 1200);
    }
  };

  const skip = async () => {
    await markNpsDismissed();
    onClose();
  };

  const sheet = (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-black/40 p-4 sm:items-center"
      style={{
        // Sit clearly above bottom nav + home indicator
        paddingBottom:
          "max(1rem, calc(var(--bottom-navigation-height, 4.5rem) + env(safe-area-inset-bottom, 0px) + 0.75rem))",
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="nps-title"
    >
      <Card className="w-full max-w-md space-y-3 p-4 shadow-lg">
        {thanks ? (
          <Text variant="body">{t("nps.thanks")}</Text>
        ) : (
          <>
            <Text variant="title" as="h2" id="nps-title" className="text-lg">
              {t("nps.title")}
            </Text>
            <Text variant="subtle" className="text-sm leading-snug">
              {t("nps.lead")}
            </Text>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: 11 }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setScore(i)}
                  className={
                    score === i
                      ? "min-h-10 min-w-10 rounded-xl bg-primary px-2 text-sm font-medium text-primary-foreground"
                      : "min-h-10 min-w-10 rounded-xl border border-border bg-surface-raised px-2 text-sm"
                  }
                >
                  {i}
                </button>
              ))}
            </div>
            {score != null && score <= 6 ? (
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                rows={2}
                placeholder={t("nps.comment")}
                className="w-full rounded-xl border border-border bg-surface-raised px-3 py-2 text-sm"
              />
            ) : null}
            <div className="flex gap-2">
              <Button
                fullWidth
                variant="ghost"
                className="min-h-11"
                onClick={() => void skip()}
              >
                {t("nps.skip")}
              </Button>
              <Button
                fullWidth
                variant="secondary"
                className="min-h-11"
                disabled={score == null || busy}
                loading={busy}
                onClick={() => void submit()}
              >
                {t("nps.submit")}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );

  return createPortal(sheet, document.body);
}
